// src/pages/institution-head/InstitutionHeadDashboard.js

import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Grid, 
    Paper, 
    Typography, 
    Box, 
    Card, 
    CardContent, 
    Divider, 
    useTheme, 
    Button, 
    useMediaQuery,
    Chip,
    IconButton
} from '@mui/material';
import { 
    SupervisorAccount, 
    People, 
    CheckCircle, 
    Refresh as RefreshIcon,
    AssignmentInd,
    HowToReg,
    Analytics,
    Domain,
    ArrowForward,
    AdminPanelSettings,
    AccountBalance as InstitutionHeadIcon,
    VerifiedUser,
    Pending
} from '@mui/icons-material';
import { apiService } from '../../apiServices/ApiService';
import { APIInstitutionHeadDashboard } from '../../apiServices/APIs';
import { getUserDetails } from '../../utils/helpers';
import LoadingState from '../../components/LoadingState';
import { useNavigate } from 'react-router-dom';
// NOTE: The component file is still named InstitutionHeadBar.js
// but it now renders "Institutional Research Lead Portal" as its visible title.
import InstitutionHeadBar from '../../components/InstitutionHeadBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const StatCard = ({ title, value, subtext, icon, color, onClick }) => (
    <Card 
        sx={{ 
            height: '100%', 
            borderRadius: 3, 
            borderLeft: `6px solid ${color}`, 
            boxShadow: 1,
            cursor: onClick ? 'pointer' : 'default',
            transition: 'all 0.3s ease',
            '&:hover': onClick ? {
                transform: 'translateY(-4px)',
                boxShadow: 6
            } : {}
        }}
        onClick={onClick}
    >
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ p: 2, bgcolor: `${color}15`, borderRadius: '50%', color: color }}>
                {icon}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{value}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>{title}</Typography>
                {subtext && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        {subtext}
                    </Typography>
                )}
            </Box>
        </CardContent>
    </Card>
);

const InstitutionHeadDashboard = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        total_university_admins: 0,
        active_admins: 0,
        inactive_admins: 0,
        pending_admins: 0,
        allowed_admins: 0,
        rejected_admins: 0
    });

    const adminId = getUserDetails("Id");
    const universityId = getUserDetails("universityId");

    const fetchDashboardStats = async () => {
        setLoading(true);
        try {
            // Fetch stats for Institutional Research Lead
            const response = await apiService.listDashboardData(
                APIInstitutionHeadDashboard,
                `${universityId}`
            );
            if (response.success && response.data) {
                setStats(response.data);
            }
        } catch (error) {
            console.error("Institutional Research Lead dashboard fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (adminId && universityId) {
            fetchDashboardStats();
        }
    }, [adminId, universityId]);

    // Navigation handlers
    const handleNavigateToAdmins = () => {
        navigate('/institution-head/institution-approval');
    };

    const handleNavigateToAllowed = () => {
        navigate('/institution-head/admin-approve?filter=allowed');
    };

    const handleNavigateToPending = () => {
        navigate('/institution-head/admin-approve?filter=pending');
    };

    if (loading) {
        return <LoadingState message="Loading dashboard data..." />;
    }

    return (
        <FullWidthContainer>
            <Box sx={{ backgroundColor: 'white', borderRadius: 3, mb: 3 }}>
                <InstitutionHeadBar notificationCount={5} />

                {/* Welcome Header */}
                <Paper elevation={1} sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    borderRadius: 3, 
                    background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                    color: 'white'
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Welcome, {getUserDetails("userName") || 'Institutional Research Lead'}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9, display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                                <Domain sx={{ fontSize: 16 }} /> {getUserDetails("university") || 'Institutional Leadership Portal'}
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                            <IconButton 
                                size="small" 
                                onClick={fetchDashboardStats} 
                                sx={{ 
                                    bgcolor: 'rgba(255,255,255,0.2)', 
                                    borderRadius: 2,
                                    color: 'white',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                                }}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                            <Chip 
                                label="Institutional Research Lead" 
                                color="warning" 
                                size="medium" 
                                variant="outlined" 
                                icon={<InstitutionHeadIcon />} 
                                sx={{ 
                                    bgcolor: 'rgba(255,255,255,0.2)', 
                                    color: 'white',
                                    borderColor: 'rgba(255,255,255,0.5)',
                                    '& .MuiChip-icon': { color: 'white' }
                                }}
                            />
                            <Chip 
                                label="System Active" 
                                color="success" 
                                size="medium" 
                                variant="outlined" 
                                icon={<CheckCircle />} 
                                sx={{ 
                                    bgcolor: 'rgba(255,255,255,0.2)', 
                                    color: 'white',
                                    borderColor: 'rgba(255,255,255,0.5)',
                                    '& .MuiChip-icon': { color: 'white' }
                                }}
                            />
                        </Box>
                    </Box>
                </Paper>
            </Box>

            {/* Metrics Dashboard Grid - University Admins Stats */}
            <Grid container spacing={3}>
                {/* Total University Admins */}
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard 
                        title="Total University Admins" 
                        value={stats.total_university_admins || 0} 
                        subtext="All registered admins"
                        icon={<AdminPanelSettings />} 
                        color={theme.palette.primary.main}
                        onClick={handleNavigateToAdmins} 
                    />
                </Grid>

                {/* Active Admins */}
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard 
                        title="Active Admins" 
                        value={stats.active_admins || 0} 
                        subtext="Currently active accounts"
                        icon={<VerifiedUser />} 
                        color={theme.palette.success.main}
                        onClick={handleNavigateToAllowed} 
                    />
                </Grid>

                {/* Pending Admins */}
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard 
                        title="Pending Admins" 
                        value={stats.pending_admins || 0} 
                        subtext="Awaiting approval"
                        icon={<Pending />} 
                        color={theme.palette.warning.main}
                        onClick={handleNavigateToPending} 
                    />
                </Grid>

                {/* Allowed Admins */}
                <Grid item xs={12} sm={6} md={6}>
                    <StatCard 
                        title="Allowed Admins" 
                        value={stats.allowed_admins || 0} 
                        subtext="Approved for access"
                        icon={<CheckCircle />} 
                        color={theme.palette.success.main}
                        onClick={handleNavigateToAllowed} 
                    />
                </Grid>

                {/* Rejected Admins */}
                <Grid item xs={12} sm={6} md={6}>
                    <StatCard 
                        title="Rejected Admins" 
                        value={stats.rejected_admins || 0} 
                        subtext="Access denied"
                        icon={<AdminPanelSettings />} 
                        color={theme.palette.error.main}
                        onClick={handleNavigateToAdmins} 
                    />
                </Grid>
            </Grid>

            {/* Quick Actions Panel */}
            <Paper sx={{ mt: 4, p: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Analytics color="warning" /> Administrative Oversight
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <Typography color="text.secondary" paragraph>
                            As the Institutional Research Lead, you have the authority to oversee and approve university administrators 
                            for your institution. Review pending applications and manage access permissions for all 
                            university administrators within your institution.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-end' } }}>
                        <Button 
                            variant="contained" 
                            color="warning"
                            endIcon={<ArrowForward />}
                            onClick={handleNavigateToAdmins}
                            sx={{
                                background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                                '&:hover': {
                                    background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.darker || '#B45309'} 100%)`,
                                }
                            }}
                        >
                            Manage Administrators
                        </Button>
                    </Grid>
                </Grid>

                {/* Quick Stats Summary */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: 'text.secondary' }}>
                        Quick Summary
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                    {stats.total_university_admins || 0}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">Total Admins</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                    {stats.allowed_admins || 0}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">Allowed</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                    {stats.pending_admins || 0}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">Pending</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                    {stats.rejected_admins || 0}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">Rejected</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </FullWidthContainer>
    );
};

export default InstitutionHeadDashboard;